<script lang="ts" setup>
  import type { QInput } from 'quasar'

  const props = withDefaults(
    defineProps<{
      clearable?: boolean
      disabled?: boolean
      enterkeyhint?: string
      error?: boolean | string
      inputmode?: string
      maxlength?: number
      modelValue?: string | number | null
      placeholder?: string
      readonly?: boolean
      showCounter?: boolean
      size?: 'medium' | 'small'
      theme?: 'default' | 'light'
      type?:
        | 'text'
        | 'password'
        | 'email'
        | 'search'
        | 'tel'
        | 'url'
        | 'number'
        | 'date'
        | 'time'
        | 'datetime-local'
        | 'textarea'
        | 'file'
    }>(),
    {
      enterkeyhint: 'default',
      inputmode: 'text',
      size: 'medium',
      type: 'text',
      theme: 'default',
      disabled: false,
      error: false,
      showCounter: false,
      readonly: false,
      clearable: false,
    },
  )

  const emit = defineEmits<{
    blur: [evt: Event]
    focus: [evt: Event]
    'update:modelValue': [value: string | number | null]
  }>()

  const inputClass = computed(() => {
    return [
      'u-input',
      `u-input--${props.size}`,
      `u-input--theme-${props.theme}`,
      {
        'u-input--error': props.error,
        'u-input--disabled': props.disabled,
      },
    ]
  })

  const errorMessage = computed(() => {
    return typeof props.error === 'string' ? props.error : ''
  })

  const showError = computed(() => {
    return !!props.error
  })

  const handleInput = (value: string | number | null) => {
    emit('update:modelValue', value)
  }

  const handleFocus = (evt: Event) => {
    emit('focus', evt)
  }

  const handleBlur = (evt: Event) => {
    emit('blur', evt)
  }

  const handleClear = () => {
    emit('update:modelValue', '')
  }
  const originalRef = ref<InstanceType<typeof QInput>>()
  defineExpose({
    originalRef,
  })
</script>

<template>
  <div class="u-input-wrapper">
    <QInput
      ref="originalRef"
      :class="inputClass"
      :model-value="modelValue"
      :placeholder="placeholder"
      :disable="disabled"
      :readonly="readonly"
      :type="type"
      :maxlength="maxlength"
      :enterkeyhint="enterkeyhint"
      :inputmode="inputmode"
      :error="showError"
      :error-message="errorMessage"
      :clearable="clearable"
      outlined
      hide-bottom-space
      @update:model-value="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @clear="handleClear"
    >
      <template
        v-if="$slots.prefix"
        #prepend
      >
        <slot name="prefix" />
      </template>
      <template
        v-if="$slots.suffix || showCounter"
        #append
      >
        <div
          v-if="showCounter && maxlength"
          class="u-input__counter"
        >
          {{ (modelValue?.toString() || '').length }}/{{ maxlength }}
        </div>
        <slot name="suffix" />
      </template>
    </QInput>
  </div>
</template>

<style lang="scss" scoped>
  .u-input-wrapper {
    width: 100%;
  }

  .u-input {
    font-family: 'Montserrat', sans-serif;

    // Sizes
    &--medium {
      :deep(.q-field__control) {
        height: 52px;
        min-height: 52px;
        padding: 0 16px 0 24px;
        border-radius: 8px;
      }

      :deep(.q-field__native) {
        font-size: 14px;
        line-height: 18px;
        font-weight: 500;
      }

      :deep(.q-field__marginal) {
        height: 52px;
      }

      :deep(.q-field__label) {
        font-size: 14px;
        line-height: 18px;
        font-weight: 500;
      }

      :deep(.q-field__control-container) {
        padding-top: 0;
      }
    }

    &--small {
      :deep(.q-field__control) {
        height: 40px;
        min-height: 40px;
        padding: 0 16px 0 24px;
        border-radius: 8px;
      }

      :deep(.q-field__native) {
        font-size: 14px;
        line-height: 18px;
        font-weight: 500;
      }

      :deep(.q-field__marginal) {
        height: 40px;
      }

      :deep(.q-field__label) {
        font-size: 14px;
        line-height: 18px;
        font-weight: 500;
      }

      :deep(.q-field__control-container) {
        padding-top: 0;
      }
    }

    // Base styles - using tenant-specific CSS variables
    :deep(.q-field__control) {
      background: var(--background-bg-surface);
      border: 1px solid var(--border-border-primary);
      border-radius: 8px;
      transition: all 0.2s ease;
      gap: 8px;

      // Override Quasar's outlined pseudo-elements
      &::before,
      &::after {
        border: none !important;
      }
    }

    :deep(.q-field__native) {
      color: var(--text-primary);

      &::placeholder {
        color: var(--text-secondary);
        opacity: 1;
      }
    }

    :deep(.q-field__label) {
      color: var(--text-secondary);
    }

    :deep(.q-field__prepend) {
      color: var(--text-content);
      font-size: 14px;
      padding: 0;
    }

    :deep(.q-field__append) {
      color: var(--text-content);
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    // Focus state
    :deep(.q-field--focused) {
      .q-field__control {
        border-color: var(--border-border-focus);
      }
    }

    // Error state
    &--error {
      :deep(.q-field__control) {
        border-color: var(--border-border-error);
      }

      :deep(.q-field__native) {
        color: var(--text-error);

        &::placeholder {
          color: var(--text-error);
        }
      }

      :deep(.q-field__label) {
        color: var(--text-error);
      }

      :deep(.q-field__prepend) {
        color: var(--color-red-600);
      }

      :deep(.q-field__append) {
        color: var(--color-red-600);
      }

      :deep(.q-field__messages) {
        color: var(--text-error);
        font-size: 12px;
        margin-top: 4px;
      }
    }

    // Disabled state
    &--disabled {
      :deep(.q-field__control) {
        background: var(--background-bg-disabled);
        border-color: var(--border-border-disabled);
      }

      :deep(.q-field__native) {
        color: var(--text-secondary);

        &::placeholder {
          color: var(--text-secondary);
        }
      }

      :deep(.q-field__label) {
        color: var(--text-secondary);
      }

      :deep(.q-field__prepend) {
        color: var(--text-secondary);
      }

      :deep(.q-field__append) {
        color: var(--text-secondary);
      }
    }

    // Light theme (for headers, etc.)
    &--theme-light {
      :deep(.q-field__native) {
        color: var(--white-1);

        &::placeholder {
          color: var(--white-1);
        }
      }

      :deep(.q-field__prepend) {
        color: var(--white-1);
      }

      :deep(.q-field__append) {
        color: var(--white-1);
      }

      &.u-input--error {
        :deep(.q-field__native) {
          color: var(--white-1);

          &::placeholder {
            color: var(--white-1);
          }
        }

        :deep(.q-field__prepend) {
          color: var(--white-1);
        }

        :deep(.q-field__append) {
          color: var(--white-1);
        }
      }
    }
  }

  .u-input__counter {
    font-family: 'Montserrat', sans-serif;
    font-size: 10px;
    line-height: 16px;
    font-weight: 400;
    color: inherit;
    white-space: nowrap;
  }

  // Hover effects
  .u-input:not(.u-input--disabled) {
    :deep(.q-field__control:hover) {
      border-color: var(--border-border-primary);
    }
  }

  .u-input--error:not(.u-input--disabled) {
    :deep(.q-field__control:hover) {
      border-color: var(--border-border-error);
    }
  }
</style>
